import React, { useState } from 'react';
import { SearchIcon, DownloadIcon } from 'lucide-react';
export function UserListReport() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportType, setReportType] = useState<'created' | 'modified'>('created');
  const [role, setRole] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Sample roles
  const roles = [{
    id: '',
    name: 'All Roles'
  }, {
    id: 'administrator',
    name: 'Administrator'
  }, {
    id: 'manager',
    name: 'Manager'
  }, {
    id: 'operator',
    name: 'Operator'
  }, {
    id: 'auditor',
    name: 'Auditor'
  }];
  // Sample user data
  const sampleUserData = [{
    id: 1,
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    role: 'Administrator',
    status: 'active',
    createdDate: '2023-05-01',
    createdBy: 'System',
    modifiedDate: '2023-05-10',
    modifiedBy: 'Admin User'
  }, {
    id: 2,
    firstName: 'Jane',
    lastName: 'Smith',
    email: 'jane.smith@example.com',
    role: 'Manager',
    status: 'active',
    createdDate: '2023-05-02',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-09',
    modifiedBy: 'Admin User'
  }, {
    id: 3,
    firstName: 'Michael',
    lastName: 'Johnson',
    email: 'michael.johnson@example.com',
    role: 'Operator',
    status: 'inactive',
    createdDate: '2023-05-03',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-08',
    modifiedBy: 'Admin User'
  }, {
    id: 4,
    firstName: 'Sarah',
    lastName: 'Williams',
    email: 'sarah.williams@example.com',
    role: 'Auditor',
    status: 'active',
    createdDate: '2023-05-04',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-07',
    modifiedBy: 'Admin User'
  }, {
    id: 5,
    firstName: 'David',
    lastName: 'Brown',
    email: 'david.brown@example.com',
    role: 'Manager',
    status: 'active',
    createdDate: '2023-05-05',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-06',
    modifiedBy: 'Admin User'
  }, {
    id: 6,
    firstName: 'Emily',
    lastName: 'Davis',
    email: 'emily.davis@example.com',
    role: 'Operator',
    status: 'locked',
    createdDate: '2023-05-06',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-05',
    modifiedBy: 'Admin User'
  }, {
    id: 7,
    firstName: 'Daniel',
    lastName: 'Miller',
    email: 'daniel.miller@example.com',
    role: 'Auditor',
    status: 'active',
    createdDate: '2023-05-07',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-04',
    modifiedBy: 'Admin User'
  }, {
    id: 8,
    firstName: 'Olivia',
    lastName: 'Wilson',
    email: 'olivia.wilson@example.com',
    role: 'Administrator',
    status: 'active',
    createdDate: '2023-05-08',
    createdBy: 'Admin User',
    modifiedDate: '2023-05-03',
    modifiedBy: 'Admin User'
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate date range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        setIsLoading(false);
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        setIsLoading(false);
        return;
      }
    }
    // Simulate API delay
    setTimeout(() => {
      // Filter sample data based on search criteria
      let results = [...sampleUserData];
      // Filter by date range based on report type
      if (fromDate && toDate) {
        const fromDateObj = new Date(fromDate);
        const toDateObj = new Date(toDate);
        results = results.filter(user => {
          const dateToCheck = new Date(reportType === 'created' ? user.createdDate : user.modifiedDate);
          return dateToCheck >= fromDateObj && dateToCheck <= toDateObj;
        });
      }
      // Filter by role if selected
      if (role) {
        results = results.filter(user => user.role.toLowerCase() === role.toLowerCase());
      }
      // Sort by the relevant date (newest first)
      results.sort((a, b) => {
        const dateA = new Date(reportType === 'created' ? a.createdDate : a.modifiedDate);
        const dateB = new Date(reportType === 'created' ? b.createdDate : b.modifiedDate);
        return dateB.getTime() - dateA.getTime();
      });
      setSearchResults(results);
      setHasSearched(true);
      setIsLoading(false);
    }, 800);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setReportType('created');
    setRole('');
    setSearchResults([]);
    setHasSearched(false);
  };
  const handleExport = (format: 'csv' | 'pdf') => {
    // In a real app, you would implement the actual export functionality
    alert(`Exporting as ${format.toUpperCase()}...`);
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'locked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-1151">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1152">User List Report</h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-1153">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-1154">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1155">
            <div className="space-y-2" data-id="element-1156">
              <label htmlFor="reportType" className="block text-sm font-medium text-gray-700" data-id="element-1157">
                Report Type
              </label>
              <select id="reportType" value={reportType} onChange={e => setReportType(e.target.value as 'created' | 'modified')} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1158">
                <option value="created" data-id="element-1159">Users Created</option>
                <option value="modified" data-id="element-1160">Users Modified</option>
              </select>
            </div>
            <div className="space-y-2" data-id="element-1161">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-1162">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1163" />
            </div>
            <div className="space-y-2" data-id="element-1164">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-1165">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1166" />
            </div>
            <div className="space-y-2" data-id="element-1167">
              <label htmlFor="role" className="block text-sm font-medium text-gray-700" data-id="element-1168">
                Role
              </label>
              <select id="role" value={role} onChange={e => setRole(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1169">
                {roles.map(role => <option key={role.id} value={role.id} data-id="element-1170">
                    {role.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1171">
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50 flex items-center" data-id="element-1172">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1173">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1174"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1175"></path>
                  </svg>
                  Generating...
                </> : <>
                  <SearchIcon size={16} className="inline mr-2" data-id="element-1176" />
                  Generate Report
                </>}
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-1177">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1178">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1179">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-1180">
              {reportType === 'created' ? 'Users Created Report' : 'Users Modified Report'}
            </h3>
            {searchResults.length > 0 && <div className="flex items-center gap-3" data-id="element-1181">
                <button onClick={() => handleExport('csv')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1182">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1183" />
                  Export CSV
                </button>
                <button onClick={() => handleExport('pdf')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1184">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1185" />
                  Export PDF
                </button>
              </div>}
          </div>
          {searchResults.length === 0 ? <div className="p-8 text-center" data-id="element-1186">
              <p className="text-gray-500" data-id="element-1187">
                No users found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1188">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1189">
                <thead className="bg-gray-50" data-id="element-1190">
                  <tr data-id="element-1191">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1192">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1193">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1194">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1195">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1196">
                      {reportType === 'created' ? 'Created Date' : 'Modified Date'}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1197">
                      {reportType === 'created' ? 'Created By' : 'Modified By'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1198">
                  {searchResults.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-1199">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1200">
                        <div className="flex items-center" data-id="element-1201">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1202">
                            {user.firstName} {user.lastName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1203">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1204">
                        {user.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1205">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(user.status)}`} data-id="element-1206">
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1207">
                        {reportType === 'created' ? user.createdDate : user.modifiedDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1208">
                        {reportType === 'created' ? user.createdBy : user.modifiedBy}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
    </div>;
}